import { createContext, useContext, useEffect, useRef, useMemo } from 'react';
import { useStore } from 'zustand';
import {
  createOrderDraftStore,
  type CreateOrderDraftStoreOptions,
  type OrderDraftStore,
} from './createOrderDraftStore';
import type { StoreApi } from 'zustand';

type OrderDraftStoreApi = StoreApi<OrderDraftStore>;

const OrderDraftContext = createContext<OrderDraftStoreApi | null>(null);

export interface OrderDraftProviderProps extends CreateOrderDraftStoreOptions {
  /**
   * Optional subscription callback fired whenever the scoped draft
   * changes. Agent confirmation cards use this to sync the staged
   * payload back to the matching entry in
   * `useAgentChatStore.pendingConfirmations`, so the confirm POST
   * always carries the latest user edits — and edits on one card
   * never bleed into a sibling card in the same turn.
   */
  onChange?: (state: OrderDraftStore) => void;
  children: React.ReactNode;
}

/**
 * React context wrapper around a scoped `createOrderDraftStore`.
 *
 * One provider per surface instance:
 *   - publish page    → wrap the route at the top (mode: `'create'`)
 *   - agent card      → wrap the `ConfirmationCard` (mode: `'create'`,
 *                       hydrated from `pending.payload`)
 *   - reorder modal   → wrap the modal contents (mode: `'reorder'`,
 *                       hydrated via `fromOrderAgainSource(order)`)
 *   - edit drawer     → wrap the drawer (mode: `'edit'`, hydrated
 *                       via `fromOrder(order, viewerOrgId)`)
 *
 * Each instance is its own scope — opening the agent card while the
 * publish page is in another tab does **not** clobber the publish
 * draft (which still uses the legacy singleton facade).
 */
export const OrderDraftProvider = ({
  mode,
  sourceOrderId,
  initialState,
  onChange,
  children,
}: OrderDraftProviderProps) => {
  const storeRef = useRef<OrderDraftStoreApi | null>(null);
  if (!storeRef.current) {
    storeRef.current = createOrderDraftStore({
      mode,
      sourceOrderId,
      initialState,
    });
  }

  // Keep a stable callback ref so subscription doesn't churn.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const store = storeRef.current;
    if (!store || !onChangeRef.current) return;
    return store.subscribe(state => {
      onChangeRef.current?.(state);
    });
  }, []);

  const value = useMemo(() => storeRef.current!, []);

  return (
    <OrderDraftContext.Provider value={value}>{children}</OrderDraftContext.Provider>
  );
};

/**
 * Read the scoped draft store. Throws if used outside of an
 * `<OrderDraftProvider>` so misuses (calling from the publish page,
 * which still uses the singleton facade) fail loud at dev time.
 */
export const useOrderDraftContext = (): OrderDraftStoreApi => {
  const ctx = useContext(OrderDraftContext);
  if (!ctx) {
    throw new Error(
      'useOrderDraftStore must be used within <OrderDraftProvider>. ' +
        'For the publish page, import usePublishOrderStore directly.',
    );
  }
  return ctx;
};

/**
 * Selector hook over the scoped draft. Mirrors the
 * `usePublishOrderStore` selector pattern, e.g.:
 *
 *   const customer = useOrderDraftStore(s => s.customer);
 */
export function useOrderDraftStore<T>(selector: (state: OrderDraftStore) => T): T {
  const store = useOrderDraftContext();
  return useStore(store, selector);
}
